/* Shipping protection toggle in the cart drawer.
 * Add/remove go straight to the Cart API, then the drawer's own getCart() rebuild runs so the
 * line list, subtotal, free-shipping bar and button total all refresh through the theme's code.
 * Preselect: adds when the cart has other items, unless the shopper explicitly unticked it this session. */
(function () {
  if (customElements.get('cart-protection')) return;

  const OPT_OUT_KEY = 'tgc_protection_optout';
  const CART_EVENTS = ['theme:cart:loaded', 'theme:cart:added', 'theme:cart:open'];

  class CartProtection extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('[data-cart-protection-toggle]');
      this.productId = this.dataset.productId;
      this.variantId = this.dataset.variantId;
      this.preselect = this.dataset.preselect === 'true';
      if (!this.toggle || !this.productId || !this.variantId) return;

      this.toggle.addEventListener('change', () => this.onToggle());
      this.onCartChange = () => this.sync();
      CART_EVENTS.forEach((name) => document.addEventListener(name, this.onCartChange));

      const holder = document.querySelector('[data-items-holder]');
      if (holder && 'MutationObserver' in window) {
        this.observer = new MutationObserver(() => this.sync());
        this.observer.observe(holder, { childList: true, subtree: true });
      }

      this.sync();
    }

    disconnectedCallback() {
      CART_EVENTS.forEach((name) => document.removeEventListener(name, this.onCartChange));
      if (this.observer) this.observer.disconnect();
    }

    get lineItem() {
      return document.querySelector(`[data-cart-item][data-product-id="${this.productId}"]`);
    }

    get otherItems() {
      return Array.from(document.querySelectorAll('[data-cart-item]')).filter((item) => item.dataset.productId !== this.productId);
    }

    get optedOut() {
      try {
        return sessionStorage.getItem(OPT_OUT_KEY) === '1';
      } catch (error) {
        return false;
      }
    }

    setOptOut(value) {
      try {
        if (value) sessionStorage.setItem(OPT_OUT_KEY, '1');
        else sessionStorage.removeItem(OPT_OUT_KEY);
      } catch (error) {
        // Storage blocked: the preselect simply repeats on the next open.
      }
    }

    cartRoot() {
      const routes = (window.theme && window.theme.routes) || {};
      return (routes.cart_url || '/cart').replace(/\/cart\/?$/, '');
    }

    url(action) {
      return `${this.cartRoot()}/cart/${action}.js`;
    }

    setLoading(state) {
      this.classList.toggle('is-loading', state);
      this.toggle.disabled = state;
    }

    refresh() {
      const cart = this.closest('cart-element') || document.querySelector('cart-element');
      if (cart && typeof cart.getCart === 'function') {
        cart.getCart();
        return;
      }
      // Fallback: nudge the theme through its own update event on another line (same quantity).
      const item = this.otherItems[0];
      const field = item && item.querySelector('[data-quantity-field]');
      if (!item || !field) return;
      item.dispatchEvent(new CustomEvent('theme:cart:update', {
        bubbles: true,
        detail: { id: item.dataset.item, quantity: parseInt(field.value, 10) || 1 },
      }));
    }

    // Automatic sync from cart events. Never runs while a request is in flight; the request's
    // own completion re-syncs from the live cart instead of the (possibly stale) drawer DOM.
    sync() {
      if (this.inFlight) return;
      const inCart = Boolean(this.lineItem);
      const others = this.otherItems.length;
      this.toggle.checked = inCart;

      if (inCart && others === 0) {
        // Protection must never be the only line in the cart.
        this.remove();
        return;
      }
      if (!inCart && others > 0 && this.preselect && !this.optedOut) this.add();
    }

    // A shopper's click is never dropped: if a request is running, remember the wanted state and
    // apply it when that request settles.
    onToggle() {
      const wanted = this.toggle.checked;
      this.setOptOut(!wanted);
      if (this.inFlight) {
        this.queued = wanted;
        return;
      }
      if (wanted) this.add();
      else this.remove();
    }

    async request(action, payload) {
      const response = await fetch(this.url(action), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        let message = `${action} failed (${response.status})`;
        try {
          const data = await response.json();
          if (data && (data.description || data.message)) message = data.description || data.message;
        } catch (error) {
          // Non-JSON error body: keep the status message.
        }
        throw new Error(message);
      }
      return response.json();
    }

    // Ground truth after a write: is the protection variant in the cart right now?
    async isInCart() {
      const response = await fetch(`${this.cartRoot()}/cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`cart.js failed (${response.status})`);
      const cart = await response.json();
      return (cart.items || []).some((item) => String(item.variant_id) === String(this.variantId));
    }

    async run(action) {
      if (this.inFlight) return;
      this.inFlight = true;
      this.setLoading(true);
      try {
        await action();
      } finally {
        this.inFlight = false;
        this.setLoading(false);
        this.refresh();

        if (this.queued !== undefined) {
          const wanted = this.queued;
          this.queued = undefined;
          if (wanted !== this.toggle.checked) {
            if (wanted) this.add();
            else this.remove();
          }
        }
      }
    }

    add() {
      return this.run(async () => {
        this.toggle.checked = true;
        try {
          if (!(await this.isInCart())) {
            await this.request('add', { items: [{ id: Number(this.variantId), quantity: 1 }] });
          }
          this.toggle.checked = await this.isInCart();
        } catch (error) {
          console.warn('cart-protection: could not add the protection product.', error);
          this.toggle.checked = false;
        }
      });
    }

    remove() {
      return this.run(async () => {
        this.toggle.checked = false;
        try {
          await this.request('change', { id: String(this.variantId), quantity: 0 });
          this.toggle.checked = await this.isInCart();
        } catch (error) {
          console.warn('cart-protection: could not remove the protection product.', error);
          this.toggle.checked = Boolean(this.lineItem);
        }
      });
    }
  }

  customElements.define('cart-protection', CartProtection);
})();
